import useAsync from 'react-use/lib/useAsync';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { Box, Chip, Typography, makeStyles } from '@material-ui/core';
import type { ProfileInfo } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  banner: {
    // Exact gradient from the approved design source (My Day.dc.html) —
    // deliberately not the brand primary purple, which never appears here.
    background: 'linear-gradient(105deg, #4f8fcf 0%, #a7cdee 100%)',
    color: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: theme.spacing(3),
    // Matches the design source's `.om-frame` container radius (14px) — the
    // mockup clips this banner's corners via an outer frame with
    // `overflow:hidden`; here the banner has no such wrapper, so it needs its
    // own radius.
    borderRadius: 14,
  },
  eyebrow: {
    letterSpacing: 1,
    opacity: 0.8,
  },
}));

// MUI's default Chip variant sets its own background/text color with higher
// specificity than a makeStyles className override, so it wins in dark theme
// (verified: the chip rendered MUI's default grey instead of this translucent
// white). Passed as inline `style` instead, which always wins.
const STATS_CHIP_STYLE = {
  backgroundColor: 'rgba(255, 255, 255, 0.16)',
  color: '#fff',
};

export function resolveFirstName(profile: ProfileInfo | undefined): string {
  const displayName = profile?.displayName?.trim();
  if (displayName) {
    return displayName.split(/\s+/)[0];
  }
  const email = profile?.email;
  if (email) {
    return email.split('@')[0];
  }
  return 'there';
}

export function greetingForHour(hour: number): string {
  if (hour < 12) {
    return 'Good morning';
  }
  if (hour < 18) {
    return 'Good afternoon';
  }
  return 'Good evening';
}

function urgencyLine(urgentCount: number): string {
  if (urgentCount === 0) {
    return 'nothing urgent today';
  }
  if (urgentCount === 1) {
    return '1 PR needs your attention';
  }
  return `${urgentCount} PRs need your attention`;
}

/** Aggregated counts shown in the header's stats pill. */
export interface MyDayStats {
  urgentCount: number;
  reviewCount: number;
  prCount: number;
  taskCount: number;
}

/**
 * Builds the header's stats-pill text. Zero-valued segments are omitted
 * (mirroring `urgencyLine`'s existing convention of not showing zero counts
 * as noise); when every count is zero, returns "All caught up" instead of an
 * empty string.
 */
export function formatStatsLine(stats: MyDayStats): string {
  const segments: string[] = [];
  if (stats.urgentCount > 0) {
    segments.push(`${stats.urgentCount} urgent`);
  }
  if (stats.reviewCount > 0) {
    segments.push(`${stats.reviewCount} to review`);
  }
  if (stats.prCount > 0) {
    segments.push(`${stats.prCount} PR${stats.prCount === 1 ? '' : 's'}`);
  }
  if (stats.taskCount > 0) {
    segments.push(`${stats.taskCount} task${stats.taskCount === 1 ? '' : 's'}`);
  }
  return segments.length > 0 ? segments.join(' · ') : 'All caught up';
}

export interface GreetingHeaderProps {
  stats: MyDayStats;
  /** True when one or more of the header's underlying stat requests failed. */
  statsError?: boolean;
}

export const GreetingHeader = ({ stats, statsError }: GreetingHeaderProps) => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const { value: profile, loading } = useAsync(
    () => identityApi.getProfileInfo(),
    [identityApi],
  );

  const now = new Date();
  const firstName = loading ? '' : resolveFirstName(profile);
  const greeting = greetingForHour(now.getHours());
  const dateText = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(now);

  return (
    <Box p={3} className={classes.banner}>
      <Box>
        <Typography variant="overline" className={classes.eyebrow}>
          Portfolio and API · My Day
        </Typography>
        <Typography variant="h4" component="h1">
          {loading ? greeting : `${greeting}, ${firstName}`}
        </Typography>
        <Typography variant="body1">
          {dateText} · {statsError ? "unable to load today's stats" : urgencyLine(stats.urgentCount)}
        </Typography>
      </Box>
      <Chip
        label={statsError ? 'Stats unavailable' : formatStatsLine(stats)}
        style={STATS_CHIP_STYLE}
      />
    </Box>
  );
};
